import React, { useEffect, useState, type FC } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { LoadableAfterPaint } from '@confluence/loadable';

import { CurrentSelectionSpan } from './CurrentSelectionSpan.tsx';
import type { SelectionRect } from './CurrentSelectionSpan.tsx';
import { READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID } from './ReadingAidsAcronymSpotlight.tsx';

const ReadingAidsAcronymSpotlight = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ReadingAidsAcronymSpotlight" */
				'./ReadingAidsAcronymSpotlight.tsx'
			)
		).ReadingAidsAcronymSpotlight,
});

type ReadingAidsSpotlightWrapperProps = {
	contentId: string;
	selectionRect: SelectionRect;
	showSpotlight: boolean;
};

/**
 * Wrapper component for the Reading Aids spotlight/onboarding UI.
 * This component is lazy-loaded so that:
 * 1. `useFeatureDiscovery` hook is only called when the wrapper is mounted
 * 2. `@atlaskit/spotlight` (PopoverContent, PopoverProvider, PopoverTarget) is only loaded when the
 *    spotlight is needed
 */
export const ReadingAidsSpotlightWrapper: FC<ReadingAidsSpotlightWrapperProps> = ({
	contentId,
	selectionRect,
	showSpotlight,
}) => {
	const [spotlightEnabled, stopSpotlight] = useFeatureDiscovery(
		READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID,
	);
	const [spotlightActive, setSpotlightActive] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const endSpotlight = () => {
		void stopSpotlight();
		setSpotlightActive(false);
	};

	useEffect(() => {
		if (spotlightEnabled && showSpotlight) {
			setSpotlightActive(true);

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					source: 'viewPageScreen',
					name: 'readingAidsAcronymSpotlight',
					attributes: {
						contentId,
					},
				},
			}).fire();
		}
	}, [contentId, createAnalyticsEvent, showSpotlight, spotlightEnabled]);

	return (
		<PopoverProvider>
			<PopoverTarget>
				<CurrentSelectionSpan
					id={READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID}
					top={selectionRect.top}
					left={selectionRect.left}
					width={selectionRect.width}
					height={selectionRect.height}
				/>
			</PopoverTarget>
			<PopoverContent
				isVisible={spotlightActive}
				placement="bottom-start"
				strategy="absolute"
				dismiss={endSpotlight}
			>
				{spotlightActive && (
					<ReadingAidsAcronymSpotlight contentId={contentId} onDismissClick={endSpotlight} />
				)}
			</PopoverContent>
		</PopoverProvider>
	);
};
